import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const VALID_PLANS = ['starter', 'growth', 'pro'];
const VALID_INDUSTRIES = ['mortgage', 'insurance', 'real_estate', 'home_services', 'coaching', 'other'];

export default function Login() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Plan chosen on the pricing page, e.g. /register?plan=growth
  const planParam = (params.get('plan') || '').toLowerCase();
  const selectedPlan = VALID_PLANS.includes(planParam) ? planParam : null;
  // Vertical landing pages (e.g. /mortgage.html) link to /register?industry=mortgage
  // so the field arrives pre-selected instead of making a warm click re-pick it.
  const industryParam = (params.get('industry') || '').toLowerCase();
  const selectedIndustry = VALID_INDUSTRIES.includes(industryParam) ? industryParam : '';
  // Land in "register" mode when arriving via /register or a ?plan= link
  const startInRegister = location.pathname === '/register' || !!selectedPlan;

  const [mode, setMode]     = useState(startInRegister ? 'register' : 'login');
  const [form, setForm]     = useState({ email: '', password: '', name: '', owner_name: '', industry: selectedIndustry });
  const [error, setError]   = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const chosenPlan = selectedPlan || 'growth';

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setNotice(''); setLoading(true);

    // The reply is deliberately identical whether or not the address has an
    // account, so there's nothing to branch on here either.
    if (mode === 'forgot') {
      try {
        const { data } = await axios.post('/api/auth/forgot-password', { email: form.email });
        setNotice(data.message || 'If that email has an account, a reset link is on its way.');
      } catch (err) {
        setError(err.response?.data?.error || 'Something went wrong');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload  = mode === 'login'
        ? { email: form.email, password: form.password }
        : { owner_email: form.email, password: form.password, name: form.name,
            owner_name: form.owner_name, plan: chosenPlan,
            industry: form.industry || null };

      const { data } = await axios.post(endpoint, payload);
      login(data.token, data.account);

      if (mode === 'register') {
        // New accounts get no app access until they've paid — send them to
        // the marketing site's preview/checkout page, not into the app.
        // Token travels in the URL fragment, never sent to any server.
        const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://velaleads.ca';
        const bizName = encodeURIComponent(data.account.name || '');
        const industryParam = encodeURIComponent(data.account.industry || '');
        window.location.href =
          `${marketingUrl}/preview.html#token=${data.token}&plan=${chosenPlan}&biz=${bizName}&industry=${industryParam}`;
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://velaleads.ca';

  return (
    <div style={pageStyle}>
      <a href={marketingUrl} style={logoStyle} aria-label="Vela home">
        <img src="/logos/vela-wordmark.png" alt="" style={{ height: 22, width: 'auto', display: 'block' }} />
      </a>

      <div style={cardStyle}>
        <h1 style={headingStyle}>
          {mode === 'login' ? 'Welcome back'
            : mode === 'forgot' ? 'Reset your password'
            : 'Create your account'}
        </h1>
        <p style={subheadStyle}>
          {mode === 'login'
            ? 'Sign in to your account'
            : mode === 'forgot'
            ? 'Enter the email your account is under and we\u2019ll send you a link.'
            : `Start your 14-day free trial on the ${chosenPlan.charAt(0).toUpperCase() + chosenPlan.slice(1)} plan`}
        </p>

        {error && <div style={errorStyle}>{error}</div>}
        {notice && <div style={noticeStyle}>{notice}</div>}

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              <label style={labelStyle}>Business Name</label>
              <input style={inputStyle} required value={form.name}
                onChange={e => set('name', e.target.value)} placeholder="Halifax Mortgage Co." />
              <label style={labelStyle}>Your Name</label>
              <input style={inputStyle} value={form.owner_name}
                onChange={e => set('owner_name', e.target.value)} placeholder="Sarah MacDonald" />
              <label style={labelStyle}>Industry</label>
              <select style={inputStyle} value={form.industry}
                onChange={e => set('industry', e.target.value)}>
                <option value="">Select your industry (optional)</option>
                <option value="mortgage">Mortgage Broker</option>
                <option value="insurance">Insurance Broker</option>
                <option value="real_estate">Real Estate</option>
                <option value="home_services">Home Services (HVAC, plumbing, etc.)</option>
                <option value="coaching">Coaching / Consulting</option>
                <option value="other">Other</option>
              </select>
            </>
          )}
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" required value={form.email}
            onChange={e => set('email', e.target.value)} placeholder="you@business.ca" />
          {mode !== 'forgot' && (
            <>
              <label style={labelStyle}>Password</label>
              <input style={inputStyle} type="password" required value={form.password}
                onChange={e => set('password', e.target.value)} placeholder="••••••••" />
            </>
          )}

          {mode === 'login' && (
            <p style={forgotRowStyle}>
              <span onClick={() => { setMode('forgot'); setError(''); setNotice(''); }}
                style={switchModeLinkStyle}>
                Forgot password?
              </span>
            </p>
          )}

          <button type="submit" disabled={loading} style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}>
            <span style={buttonTextStyle}>
              {loading ? 'Please wait…'
                : mode === 'login' ? 'Sign In'
                : mode === 'forgot' ? 'Send reset link'
                : 'Create Account'}
            </span>
          </button>
        </form>

        <p style={switchModeStyle}>
          {mode === 'forgot' ? (
            <span onClick={() => { setMode('login'); setError(''); setNotice(''); }}
              style={switchModeLinkStyle}>
              Back to sign in
            </span>
          ) : (
            <>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <span onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setNotice(''); }}
                style={switchModeLinkStyle}>
                {mode === 'login' ? 'Sign up free' : 'Sign in'}
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', gap: 28, background: 'var(--vela-bg)', padding: '40px 20px',
};
const logoStyle = {
  display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
};
const cardStyle = {
  background: 'var(--vela-glass-bg)', backdropFilter: 'blur(24px) saturate(160%)',
  WebkitBackdropFilter: 'blur(24px) saturate(160%)', border: '1px solid var(--vela-glass-border)',
  borderRadius: 20, padding: '40px 36px', width: 380, maxWidth: '100%',
};
const headingStyle = {
  fontFamily: "'Inter', -apple-system, sans-serif", fontWeight: 600, fontSize: 22,
  color: 'var(--vela-text)', marginBottom: 6, lineHeight: 1.15, textAlign: 'center',
};
const subheadStyle = { color: 'var(--vela-text-mid)', marginBottom: 28, fontSize: 14, textAlign: 'center' };
const errorStyle = {
  background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
  color: '#fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13,
};
const noticeStyle = {
  background: 'rgba(166,227,208,0.10)', border: '1px solid rgba(166,227,208,0.30)',
  color: '#a6e3d0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13,
};
const forgotRowStyle = { textAlign: 'right', fontSize: 13, marginTop: -6, marginBottom: 4 };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5, color: 'var(--vela-text-mid)' };
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--vela-glass-border-bright)',
  background: 'var(--vela-glass-bg)', color: 'var(--vela-text)',
  borderRadius: 8, fontSize: 14, marginBottom: 14, outline: 'none', fontFamily: 'inherit',
};
const buttonStyle = {
  width: '100%', position: 'relative', overflow: 'hidden', border: 'none',
  borderRadius: 100, padding: '13px', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', marginTop: 8, color: 'var(--primary-contrast)',
  backgroundImage: 'var(--vela-opal-grad)',
};
const buttonTextStyle = { position: 'relative', zIndex: 1 };
const switchModeStyle = { textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--vela-text-mid)' };
const switchModeLinkStyle = { color: 'var(--vela-text)', cursor: 'pointer', fontWeight: 500 };
